import { all, put, call, takeLatest, select } from 'redux-saga/effects';
import {
  createIncidentReportFailure,
  createIncidentReportRequest,
  createIncidentReportSuccess,
  incidentReportFailure,
  incidentReportRequest,
  incidentReportsFailure,
  incidentReportsRequest,
  incidentReportsSuccess,
  incidentReportSuccess,
  incidentReportLinkRequest,
  incidentReportLinkSuccess,
  incidentReportLinkFailure,
  deleteIncidentReportSuccess,
  deleteIncidentReportFailure,
  deleteIncidentReportRequest,
  pendingIncidentReportFailure,
  pendingIncidentReportRequest,
  pendingIncidentReportSuccess,
  approveIncidentReportFailure,
  approveIncidentReportRequest,
  approveIncidentReportSuccess,
  updateIncidentReportFailure,
  updateIncidentReportRequest,
  updateIncidentReportSuccess,
} from './actions';
import axios from 'axios';
import { INCIDENT_REPORTS, INCIDENT_REPORTS_V2 } from 'app/api-routes';
import appConfig from 'config';
import { prepareFieldValueByType } from 'ducks/utils/form-builder';
import { TABLE_NAMES } from 'app/types/constants';
import { bulkUploadMediaAssetsSaga, deleteMediaAssetSaga } from 'ducks/mediaAssets/sagas';
import { MediaAssetTypes } from 'ducks/mediaAssets/types';
import { selectIncidentReport } from './selectors';

const hasFieldValue = (value) => {
  if (value === null || typeof value === 'undefined') {
    return false;
  }

  if (value instanceof Date) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return true;
};

function* serializeIncidentReportFields(fields = {}, entityID) {
  const serializedFields = [];

  const entries = Object.entries(fields);

  for (let index = 0; index < entries.length; index++) {
    const [fieldID, fieldValue] = entries[index];

    if (!fieldValue) {
      continue;
    }

    const { fieldResultID, value, type } = fieldValue;

    if (!hasFieldValue(value)) {
      continue;
    }

    const serializedValue = yield call(prepareFieldValueByType, {
      type,
      value,
      entityID,
      tableName: TABLE_NAMES.INCIDENT_REPORT,
      category: TABLE_NAMES.INCIDENT_REPORT_FIELD_RESULTS,
    });

    const parsedFieldID = Number(fieldID);

    serializedFields.push({
      fieldResultID,
      fieldID: Number.isNaN(parsedFieldID) ? fieldID : parsedFieldID,
      value: serializedValue,
      incidentReportID: entityID,
    });
  }

  return serializedFields;
}

function* getIncidentReportLinkSaga({ payload: reportId }) {
  try {
    const { payload } = yield axios.get(`${INCIDENT_REPORTS}/${reportId}/report`);

    if (payload) {
      yield put(
        incidentReportLinkSuccess({
          reportId,
          link: `${appConfig.serverURL}/api/mediaassets/stream/${payload}`,
        })
      );
    }
  } catch (err) {
    yield put(incidentReportLinkFailure(err));
  }
}

function* incidentReportsSaga() {
  try {
    const { payload } = yield axios.get(INCIDENT_REPORTS);

    yield put(incidentReportsSuccess(payload));
  } catch (err) {
    yield put(incidentReportsFailure(err));
  }
}

export function* incidentReportSaga({ payload: reportID, meta }) {
  try {
    const { payload } = yield axios.get(`${INCIDENT_REPORTS_V2}/${reportID}`);

    yield put(incidentReportSuccess(payload));
    meta?.onSuccess?.(payload);
  } catch (err) {
    yield put(incidentReportFailure(err));
    meta?.onFailure?.(err);
  }
}

function* createIncidentReportSaga({ payload: { values, fields, photos }, meta }) {
  try {
    const { payload: reportID } = yield axios.post(INCIDENT_REPORTS_V2, {
      payload: values,
    });

    if (fields && reportID) {
      const serializedFields = yield call(serializeIncidentReportFields, fields, reportID);

      if (serializedFields.length) {
        yield axios.post(`${INCIDENT_REPORTS_V2}/${reportID}/fields`, {
          payload: serializedFields,
        });
      }
    }

    if (photos?.length && reportID) {
      const files = photos.map((item) => ({
        fileUploaded: item.photo,
        mediaAssetInfo: {
          entityID: reportID,
          description: item.description,
          tableName: TABLE_NAMES.INCIDENT_REPORT,
          type: MediaAssetTypes.PHOTO,
        },
      }));

      yield call(bulkUploadMediaAssetsSaga, { payload: { files } });
    }

    yield put(createIncidentReportSuccess({ reportID }));
    meta?.onSuccess?.(reportID);
  } catch (err) {
    yield put(createIncidentReportFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* updateIncidentReportSaga({ payload: { reportID, values, fields, photos }, meta }) {
  try {
    yield axios.put(`${INCIDENT_REPORTS_V2}/${reportID}`, {
      payload: values,
    });

    if (fields && reportID) {
      const serializedFields = yield call(serializeIncidentReportFields, fields, reportID);

      if (serializedFields.length) {
        yield axios.post(`${INCIDENT_REPORTS_V2}/${reportID}/fields`, {
          payload: serializedFields,
        });
      }
    }

    if (Array.isArray(photos) && reportID) {
      const incidentReport = yield select((state) => selectIncidentReport(state, reportID));
      const existingPhotoIDs = incidentReport?.photoIDs || [];
      const nextPhotoIDs = photos
        .filter(({ mediaAssetID }) => mediaAssetID)
        .map(({ mediaAssetID }) => mediaAssetID);

      const removedPhotoIDs = existingPhotoIDs.filter((mediaAssetID) => !nextPhotoIDs.includes(mediaAssetID));

      for (let index = 0; index < removedPhotoIDs.length; index++) {
        const mediaAssetID = removedPhotoIDs[index];

        yield call(deleteMediaAssetSaga, { payload: mediaAssetID });
      }

      const newPhotos = photos.filter((item) => !item.mediaAssetID && item.photo?.file);

      if (newPhotos.length) {
        const files = newPhotos.map((item) => ({
          fileUploaded: item.photo,
          mediaAssetInfo: {
            entityID: reportID,
            description: item.description,
            tableName: TABLE_NAMES.INCIDENT_REPORT,
            type: MediaAssetTypes.PHOTO,
          },
        }));

        yield call(bulkUploadMediaAssetsSaga, { payload: { files } });
      }
    }

    yield put(updateIncidentReportSuccess({ reportID }));
    meta?.onSuccess?.(reportID);
  } catch (err) {
    yield put(updateIncidentReportFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* deleteIncidentReportSaga({ payload: { reportID, password }, meta }) {
  try {
    yield axios.delete(`${INCIDENT_REPORTS_V2}/${reportID}`, { data: { payload: password } });

    yield put(deleteIncidentReportSuccess(reportID));
    meta?.onSuccess?.(reportID);
  } catch (err) {
    yield put(deleteIncidentReportFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* pendingIncidentReportSaga({ payload: { reportID, pendingReason }, meta }) {
  try {
    yield axios.post(`${INCIDENT_REPORTS_V2}/${reportID}/pending`, {
      payload: {
        pendingReason,
      },
    });

    yield put(
      pendingIncidentReportSuccess({
        reportID,
        pendingReason,
      })
    );

    meta?.onSuccess?.({ reportID, pendingReason });
  } catch (err) {
    yield put(pendingIncidentReportFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* approveIncidentReportSaga({ payload: { reportID }, meta }) {
  try {
    yield axios.post(`${INCIDENT_REPORTS_V2}/${reportID}/approve`);

    yield put(
      approveIncidentReportSuccess({
        reportID,
      })
    );

    meta?.onSuccess?.({ reportID });
  } catch (err) {
    yield put(approveIncidentReportFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

export default function* () {
  yield all([
    yield takeLatest(incidentReportsRequest, incidentReportsSaga),
    yield takeLatest(incidentReportRequest, incidentReportSaga),
    yield takeLatest(createIncidentReportRequest, createIncidentReportSaga),
    yield takeLatest(updateIncidentReportRequest, updateIncidentReportSaga),
    yield takeLatest(deleteIncidentReportRequest, deleteIncidentReportSaga),
    yield takeLatest(incidentReportLinkRequest, getIncidentReportLinkSaga),
    yield takeLatest(pendingIncidentReportRequest, pendingIncidentReportSaga),
    yield takeLatest(approveIncidentReportRequest, approveIncidentReportSaga),
  ]);
}
